/*
 * The three-state airflow demo.
 *
 * All the work is done by CSS off a single `data-state` attribute — this file
 * exists only to move that attribute and keep the tab semantics honest. It
 * reuses the reg finder's roving-tabindex behaviour so the two segmented
 * controls on the page are operated the same way from the keyboard.
 */
(() => {
  if (customElements.get('airflux-demo')) return;

  class AirfluxDemo extends HTMLElement {
    connectedCallback() {
      this.tabs = [...this.querySelectorAll('[data-demo-state]')];
      this.scene = this.querySelector('[data-demo-scene]');
      // The drawing is what `data-state` drives; the panel is what the tabs
      // control, and it is the larger of the two — it holds the verdict as well.
      // Falling back to the scene keeps this working against older markup.
      this.panel = this.querySelector('[data-demo-panel]') || this.scene;
      if (!this.tabs.length || !this.scene) return;

      this.addEventListener('click', (event) => {
        const tab = event.target.closest('[data-demo-state]');
        if (tab) this.select(tab);
      });

      // Left/right walks the group, home/end jump to its ends — the pattern a
      // tablist is expected to follow, and the one the reg finder already uses.
      this.addEventListener('keydown', (event) => {
        const index = this.tabs.indexOf(document.activeElement);
        if (index < 0) return;
        const last = this.tabs.length - 1;
        const next = {
          ArrowRight: index + 1 > last ? 0 : index + 1,
          ArrowLeft: index - 1 < 0 ? last : index - 1,
          Home: 0,
          End: last,
        }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        this.select(this.tabs[next]);
        this.tabs[next].focus();
      });

      this.select(this.tabs.find((t) => t.getAttribute('aria-selected') === 'true') || this.tabs[0]);
      this.watchVisibility();
    }

    /**
     * Stop animating once the drawing has scrolled away.
     *
     * 🔴 It did not, and that is the whole reason this observer exists. The
     * airflow is drawn with `stroke-dashoffset` animations running
     * `3s linear infinite` (plus a 7s trickle), gated only by `data-state` —
     * and `data-state` is ALWAYS one of the three, so one flow group was always
     * running. Not while the section was on screen: for the rest of the visit.
     * Somebody who scrolled past this section on the way to the reviews left it
     * repainting behind them until they closed the tab.
     *
     * `stroke-dashoffset` is the expensive half of that. Unlike `transform` and
     * `opacity`, it is not a property the compositor can animate on its own, so
     * every frame is a real repaint of the SVG rather than the GPU moving a
     * layer it already has. Off screen that work buys nothing at all.
     *
     * The pattern is not new here — `airflux-wipe.js` and `animations.js` both
     * already gate on an observer. This section simply never got one.
     *
     * `rootMargin` keeps a screen of slack either side so the animation is
     * already running by the time the drawing is scrolled to, rather than
     * visibly starting once it arrives.
     */
    watchVisibility() {
      if (!('IntersectionObserver' in window)) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) this.toggleAttribute('data-idle', !entry.isIntersecting);
        },
        { rootMargin: '100% 0px' }
      );
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.observer = null;
    }

    select(tab) {
      for (const other of this.tabs) {
        const on = other === tab;
        other.setAttribute('aria-selected', String(on));
        other.tabIndex = on ? 0 : -1;
      }
      this.scene.dataset.state = tab.dataset.demoState;
      // Three tabs share one panel, so the panel's accessible name has to
      // follow the selection rather than be set once in the markup.
      this.panel.setAttribute('aria-labelledby', tab.id);

      for (const panel of this.querySelectorAll('[data-demo-verdict]')) {
        panel.toggleAttribute('hidden', panel.dataset.demoVerdict !== tab.dataset.demoState);
      }
    }
  }

  customElements.define('airflux-demo', AirfluxDemo);
})();
